import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { msalConfig, loginRequest } from '../authConfig';
import { getMsalInstance } from '../index';
import api from '../services/api';

export const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const msalInstance = getMsalInstance();

  useEffect(() => {
    const loadUser = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('research_token');
        console.log('[AuthContext] loadUser: storedUser:', storedUser, 'token:', token);
        console.log('[AuthContext] Loading user from storage:', { hasUser: !!storedUser, hasToken: !!token });
        if (storedUser && token) {
          const userData = JSON.parse(storedUser);
          console.log('[AuthContext] Found stored user and token, setting user:', userData);
          setUser(userData); // Set user immediately from storage

          try {
            console.log('[AuthContext] Calling /auth/verify with token to refresh user data');
            const response = await api.get('/auth/verify');
            console.log('[AuthContext] /auth/verify response:', response);
            if (response.data?.user) {
              console.log('[AuthContext] Token verified, updating user with fresh data:', response.data.user);
              setUserWithPersistence(response.data.user);
            }
          } catch (error) {
            console.warn('[AuthContext] Token verification failed, but keeping stored user:', error.response?.status, error.response?.data);
            // Don't logout immediately - keep the stored user for now
            // Only logout if it's a 401 (unauthorized) error
            if (error.response?.status === 401) {
              console.log('[AuthContext] 401 error - token is invalid, logging out');
              logout();
            }
          }
        } else {
          console.log('[AuthContext] No stored user or token found');
          setUser(null);
        }
      } catch (error) {
        console.error('[AuthContext] Error loading user from storage:', error);
        setUser(null);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    // Load user immediately - MSAL is already initialized in index.js
    loadUser();
    const handleStorage = (event) => {
      if (event.key === 'user' || event.key === 'research_token') {
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('research_token');
        console.log('[AuthContext] Storage event: user:', storedUser, 'token:', token);
        if (storedUser && token) {
          setUser(JSON.parse(storedUser));
        } else {
          setUser(null);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const login = async (credentials) => {
    try {
      console.log('[AuthContext] Attempting traditional login with credentials:', credentials);
      const response = await api.post('/auth/login', credentials);
      console.log('[AuthContext] /auth/login response:', response);
      if (response.data.success) {
        const { access_token, user: userData } = response.data;
        setUserWithPersistence(userData);
        localStorage.setItem('research_token', access_token);
        console.log('[AuthContext] Traditional login successful:', userData, 'token:', access_token);
      }
      return response.data;
    } catch (error) {
      console.error('[AuthContext] Login error in context:', error);
      throw error;
    }
  };

  const logout = useCallback(async () => {
    console.trace('[AuthContext] Logging out user (trace)');

    try {
      // Get all accounts from MSAL
      const accounts = msalInstance.getAllAccounts();
      console.log('[AuthContext] MSAL accounts found for logout:', accounts.length);

      if (accounts.length > 0) {
        // Clear all accounts and their cached tokens
        for (const account of accounts) {
          try {
            console.log('[AuthContext] Clearing MSAL account:', account.username);
            // Remove account and all associated tokens from cache
            await msalInstance.getTokenCache().removeAccount(account);
          } catch (error) {
            console.error('[AuthContext] Error removing MSAL account:', error);
          }
        }

        // Perform popup logout for the active account
        const currentAccount = msalInstance.getActiveAccount();
        if (currentAccount) {
          try {
            console.log('[AuthContext] Performing MSAL popup logout');
            await msalInstance.logoutPopup({
              account: currentAccount,
              postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri
            });
          } catch (error) {
            console.error('[AuthContext] MSAL popup logout error:', error);
          }
        }

        // Clear active account
        msalInstance.setActiveAccount(null);
        console.log('[AuthContext] MSAL active account cleared');
      }
    } catch (error) {
      console.error('[AuthContext] MSAL logout error:', error);
    }

    // Clear local storage and state
    localStorage.removeItem('research_token');
    localStorage.removeItem('user');
    setUser(null);
    console.log('[AuthContext] Local storage and user state cleared');
  }, [msalInstance]);

  const loginWithMicrosoft = async () => {
    try {
      console.log('[AuthContext] Starting Microsoft login with MSAL');

      // Use MSAL's built-in login with PKCE
      const loginResponse = await msalInstance.loginPopup(loginRequest);
      console.log('[AuthContext] MSAL login response:', loginResponse);

      if (loginResponse.account) {
        // Get access token using MSAL
        const tokenRequest = {
          ...loginRequest,
          account: loginResponse.account
        };

        const tokenResponse = await msalInstance.acquireTokenSilent(tokenRequest);
        console.log('[AuthContext] MSAL token response:', tokenResponse);

        // Send the access token to backend for user info
        const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${apiUrl}/auth/msal/token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${tokenResponse.accessToken}`
          },
          body: JSON.stringify({
            access_token: tokenResponse.accessToken,
            account: loginResponse.account
          })
        });

        console.log('[AuthContext] Backend token validation response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[AuthContext] Backend validation failed:', errorText);
          throw new Error(`Backend validation failed: ${response.status}`);
        }

        const data = await response.json();
        console.log('[AuthContext] Backend validation success:', data);

        if (data.user && data.access_token) {
          // Store token first
          localStorage.setItem('research_token', data.access_token);
          // Then set user with persistence (this will also store user in localStorage)
          setUserWithPersistence(data.user);
          console.log('[AuthContext] Microsoft SSO login successful, user set:', data.user, 'JWT token stored');
          return data.user;
        } else {
          throw new Error("No user or access_token returned from backend validation.");
        }
      } else {
        throw new Error("No account returned from MSAL login.");
      }
    } catch (err) {
      console.error('[AuthContext] Microsoft login error:', err);

      // Handle specific MSAL errors with better user messages
      if (err.name === 'BrowserAuthError') {
        if (err.errorCode === 'popup_window_error' || err.errorCode === 'empty_window_error') {
          throw new Error("Popup was blocked or closed. Please allow popups for this site and try again.");
        }
        if (err.errorCode === 'user_cancelled') {
          throw new Error("Authentication was cancelled. Please try again.");
        }
        if (err.errorCode === 'monitor_window_timeout') {
          throw new Error("Authentication timed out. Please try again.");
        }
        throw new Error("Browser error during authentication. Please try again.");
      }

      if (err.name === 'ClientAuthError') {
        if (err.errorCode === 'multiple_matching_tokens') {
          throw new Error("Multiple accounts found. Please clear your browser cache and try again.");
        }
        throw new Error("Authentication configuration error. Please contact support.");
      }

      if (err.name === 'ServerError') {
        throw new Error("Server error during authentication. Please try again later.");
      }

      if (err.name === 'InteractionRequiredAuthError') {
        throw new Error("Interactive authentication required. Please try again.");
      }

      // Generic error message for unknown errors
      throw new Error("Microsoft login failed: " + (err.message || err));
    }
  };

  const normalizeRole = (role) =>
    typeof role === 'string'
      ? role.toLowerCase().replace(/\s+/g, '_')
      : (role?.name || '').toLowerCase().replace(/\s+/g, '_');

  const setUserWithPersistence = useCallback((userData) => {
    if (userData && userData.role) {
      userData = { ...userData, role: normalizeRole(userData.role) };
    }
    console.log('Setting user with persistence:', userData);
    setUser(userData);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    }
  }, []);

  const value = {
    user,
    setUser: setUserWithPersistence,
    login,
    logout,
    loginWithMicrosoft,
    loading,
    isInitialized
  };

  return (
    <AuthContext.Provider value={value}>
      {isInitialized ? children : (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export default { AuthContext, useAuth, AuthProvider };